import React, { useEffect, useState } from "react";
import { Formik, Form, FieldArray } from "formik";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  TextField,
  Button,
  Typography,
  IconButton,
  MenuItem,
  Box,
  Card,
  CardContent
} from "@mui/material";
import { FaTrash } from "react-icons/fa";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";
import ContractViewController from "../../redux/actions/ContractApiAction";
import { FadeLoader } from "react-spinners";

const contractTypeOptions = [
  { label: "Probation", value: "probation" },
  { label: "Permanent", value: "permanent" },
  { label: "Contract", value: "contract" },
  { label: "Consultant", value: "consultant" },
];

const TremsAndConditionForm = ({ show, handleClose, initialData = null }) => {
  const dispatch = useDispatch();
  const { addContractTermsAndConditions, updateContractTermAndConditions } =
    ContractViewController();
  const isEdit = Boolean(initialData?.id);
  const { isLoading } = useSelector((state) => state.master);

  const [initialValues, setInitialValues] = useState({
    contract_type: "permanent",
    terms_and_conditions: [{ title: "", description: "" }],
  });

  useEffect(() => {
    if (show) {
      if (initialData) {
        setInitialValues({
          contract_type: initialData.contract_type,
          terms_and_conditions:
            initialData.terms_and_conditions || [{ title: "", description: "" }],
        });
      } else {
        setInitialValues({
          contract_type: "permanent",
          terms_and_conditions: [{ title: "", description: "" }],
        });
      }
    }
  }, [show, initialData]);

  const handleSubmit = (values, { resetForm }) => {
    const action = isEdit
      ? updateContractTermAndConditions(initialData.id, values)
      : addContractTermsAndConditions(values);

    dispatch(action)
      .then(() => {
        resetForm();
        handleClose();
      })
      .catch((error) => {
        console.error("Error submitting Terms & Conditions:", error);
      });
  };

  return (
    <Dialog open={show} onClose={handleClose} maxWidth="xl" fullWidth>
      <DialogTitle
        sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
      >
        {isEdit ? "Edit Terms & Conditions" : "Add Terms & Conditions"}
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ maxHeight: "80vh", p: 0 }}>
        <Formik initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize>
          {({ values, handleChange, setFieldValue }) => (
            <Form>
              {/* Overlay Loader inside Form */}
              {isLoading && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    backgroundColor: "rgba(255, 255, 255, 0.6)",
                    zIndex: 9999,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <FadeLoader loading={true} size={150} />
                </Box>
              )}

              <Box sx={{ p: 3 }}>
                <Grid container spacing={2}>
                  {/* Contract Type */}
                  <Grid item xs={12}>
                    <TextField
                      select
                      fullWidth
                      label="Contract Type"
                      name="contract_type"
                      value={values.contract_type}
                      onChange={handleChange}
                    >
                      {contractTypeOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {/* Dynamic Terms */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" gutterBottom>
                      Terms & Conditions
                    </Typography>

                    <FieldArray
                      name="terms_and_conditions"
                      render={(arrayHelpers) => (
                        <Box>
                          {values.terms_and_conditions.map((term, index) => (
                            <Card key={index} variant="outlined" sx={{ mb: 2 }}>
                              <CardContent>
                                <Box
                                  display="flex"
                                  justifyContent="space-between"
                                  alignItems="center"
                                  mb={2}
                                >
                                  <Typography variant="h6">
                                    {`Term & Condition ${index + 1}`}
                                  </Typography>
                                  <IconButton
                                    color="error"
                                    onClick={() => arrayHelpers.remove(index)}
                                    aria-label="delete-term"
                                  >
                                    <FaTrash />
                                  </IconButton>
                                </Box>

                                <TextField
                                  fullWidth
                                  label="Title"
                                  name={`terms_and_conditions[${index}].title`}
                                  value={term.title}
                                  onChange={handleChange}
                                  sx={{ mb: 2 }}
                                />

                                <TextField
                                  fullWidth
                                  multiline
                                  minRows={3}
                                  label="Description"
                                  name={`terms_and_conditions[${index}].description`}
                                  value={term.description}
                                  onChange={handleChange}
                                />
                              </CardContent>
                            </Card>
                          ))}

                          <Button
                            variant="contained"
                            size="small"
                            onClick={() =>
                              setFieldValue("terms_and_conditions", [
                                ...values.terms_and_conditions,
                                { title: "", description: "" },
                              ])
                            }
                          >
                            Add Terms
                          </Button>
                        </Box>
                      )}
                    />
                  </Grid>
                </Grid>

                {/* ✅ Submit & Cancel Buttons inside Form */}
                <Box
                  sx={{
                    borderTop: "1px solid #ddd",
                    mt: 3,
                    pt: 2,
                    display: "flex",
                    justifyContent: "flex-end",
                    background: "#fff",
                    position: "sticky"
                  }}
                >
                  <Button variant="outlined" onClick={handleClose} sx={{ mr: 2 }}>
                    Cancel
                  </Button>
                  <Button type="submit" variant="contained" color="primary">
                    {isEdit ? "Update" : "Submit"}
                  </Button>
                </Box>
              </Box>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default TremsAndConditionForm;
